import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useMemo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { ApplicationStatus, PaginatedResponse } from "shared/types";
import {
  BaseOption,
  Button,
  InputSize,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  Show,
  IconButtonVariant,
  Select
} from "shared/components";
import { FilterLines, Plus } from "untitledui-js-base";
import { BaseInput } from "shared/components/forms/BaseInput";
import { ApplicationsDataGridTable } from "./components/Table/ApplicationsDataGridTable";
import { Pagination } from "shared/components/Pagination";
import { Applicant, Application } from "types/application";
import { RowSelectionState } from "@tanstack/react-table";
import { MergeApplicantsModal } from "./components/MergeApplicantsModal";
import { CancelOrRefundModal } from "../CancelOrRefundModal";
import queryString from "qs";
import { MoveModal } from "../MoveModal";
import { FiltersDrawer } from "./components/FiltersDrawers";
import { FilterBadge } from "./components/FiltersDrawers/FilterBadge";
import { AssigneeFilterPopover } from "shared/components/Assignee";
import { SelectPopUp } from "./components/Table/SelectPopUp";
import { RefundModal } from "../RefundModal";
import { useMedia } from "shared/providers";
import { InviteAndCreateModal } from "../InviteAndCreateModal";
import { UncancelModal } from "../UncancelModal";
import { ColumnsMenu } from "./components/ColumnsMenu";
import { getErrorMessage } from "utils/error";
import {
  DEFAULT_COLUMN_VISIBILITY,
  DEFAULT_PER_PAGE,
  NO_UNIT_TYPE,
  sortableKeys
} from "./constants";
import { buildAssigneesParam } from "shared/components/Assignee";
import { useAuthContext } from "providers/Auth";
import { useMessagePopupStore } from "shared/app/MessagePopup/store";
import { ApplicantStatus, ApplicantType } from "shared/types";
import { SplitModal } from "../SplitModal";
import { ActiveTab } from "./components/Table/constants";
import {
  ApplicationTabs,
  ApplicationTabItem
} from "./components/ApplicationTabs";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useApplicationSummary } from "shared/hooks/use-application-summary";
import { useApplicationQueryParams } from "./hooks/useApplicationQueryParams";
import { useLocation } from "react-router-dom";
import { Notes } from "../Notes";

const query = "/portal/v1/applications";

const mergeColumnsWithDefaults = (
  savedColumns:
    | { id: string; label: string; checked: boolean; disabled: boolean }[]
    | undefined
) => {
  if (!savedColumns) return DEFAULT_COLUMN_VISIBILITY;

  const savedColumnIds = new Set(
    savedColumns.map((col: { id: string }) => col.id)
  );
  const mergedColumns = [...savedColumns];

  DEFAULT_COLUMN_VISIBILITY.forEach((defaultCol) => {
    if (!savedColumnIds.has(defaultCol.id)) mergedColumns.push(defaultCol);
  });

  return mergedColumns;
};

const addAssigneeIds = (app: Application) => ({
  ...app,
  assignee_ids: app.assignees?.map((a) => a.id) || []
});

const applicationTabLabels: { value: ActiveTab; label: string }[] = [
  { value: ActiveTab.All, label: "All" },
  { value: ActiveTab.Submitted, label: "Submitted" },
  { value: ActiveTab.PartiallySubmitted, label: "Partially submitted" },
  { value: ActiveTab.NotSubmitted, label: "Not submitted" },
  { value: ActiveTab.OnHold, label: "On hold" },
  { value: ActiveTab.Approved, label: "Approved" },
  { value: ActiveTab.ApprovedConditionally, label: "Approved conditionally" },
  { value: ActiveTab.Declined, label: "Declined" },
  { value: ActiveTab.Canceled, label: "Canceled" }
];

const buildApplicationTabs = (counts: {
  submitted?: number;
  partiallySubmitted?: number;
}): ApplicationTabItem[] =>
  applicationTabLabels.map((tab) => {
    if (tab.value === ActiveTab.Submitted)
      return { ...tab, count: counts.submitted };
    if (tab.value === ActiveTab.PartiallySubmitted)
      return { ...tab, count: counts.partiallySubmitted };
    return tab;
  });

export type { ApplicationFilters } from "./hooks/useApplicationQueryParams";

export const ApplicationsTableView = ({
  propertyId,
  unitId,
  contactId,
  viewPortHeightOffset,
  persistParams = false
}: {
  propertyId?: number;
  unitId?: number;
  contactId?: number;
  viewPortHeightOffset?: number;
  persistParams?: boolean;
}) => {
  const { isMobile } = useMedia();
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedRentersRow, setSelectedRentersRow] =
    useState<RowSelectionState>({});
  const { auth, type, refreshAuthUserDashboardState } = useAuthContext();

  const [cancelModalId, setCancelModalId] = useState<string | undefined>();
  const [uncancelModalId, setUncancelModalId] = useState<string | undefined>();
  const [moveModalId, setMoveModalId] = useState<string | undefined>();
  const [splitModalId, setSplitModalId] = useState<string | undefined>();
  const [isShowModal, setIsShowModal] = useState(false);
  const [showApplicationNotesId, setShowApplicationNotesId] = useState<
    number | undefined
  >();
  const dictionary = useUnitsPropertiesDictionary();

  const [showFilters, setShowFilters] = useState(false);
  const [refundModalId, setRefundModalId] = useState<string | undefined>();
  const [isAssigneeFilterOpen, setIsAssigneeFilterOpen] = useState(false);

  const location = useLocation();

  const {
    filters,
    searchParams,
    sorting,
    groupBy,
    searchText,
    isInitialized,
    assigneeIds,
    setFilters,
    setSearchParams,
    setGroupBy,
    setAssigneeIds,
    handleSortingChange,
    handleRemoveProperty,
    handleRemoveCity,
    handleRemoveUnit,
    handleRemoveUnitType,
    handleRemoveScreeningStatus,
    handleRemoveUnitStatus,
    handleRemovePropertyGroup
  } = useApplicationQueryParams({ persistParams });

  const dataQuery = useApplicationSummary(cancelModalId || undefined);

  const { applicants } = dataQuery.data || {};

  const updateColumnVisibility = useMutation({
    mutationKey: ["updateColumnVisibility"],
    mutationFn: (columnsVisibility: Record<string, unknown>) =>
      instance.patch(`/portal/v1/users/${auth?.id}`, {
        columns_visibility: columnsVisibility
      })
  });

  const [columnsVisibility, setColumnsVisibility] = useState(() =>
    mergeColumnsWithDefaults(
      auth?.columns_visibility?.[searchParams.tab.toLowerCase()]
    )
  );
  const [totalColumnsVisibility, setTotalColumnsVisibility] = useState<
    Partial<
      Record<
        string,
        { id: string; label: string; checked: boolean; disabled: boolean }[]
      >
    >
  >(auth?.columns_visibility || {});

  useEffect(() => {
    const savedColumns = totalColumnsVisibility[searchParams.tab.toLowerCase()];
    setColumnsVisibility(mergeColumnsWithDefaults(savedColumns));
  }, [searchParams.tab, totalColumnsVisibility]);

  const handleColumnToggle = async (id: string, checked: boolean) => {
    const updateColumnsVisibility = columnsVisibility.map((col) =>
      col.id === id ? { ...col, checked } : col
    );
    setColumnsVisibility(updateColumnsVisibility);

    const updatedColumnsVisibility = {
      ...totalColumnsVisibility,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- tab can be undefined
      [searchParams.tab?.toLowerCase()]: updateColumnsVisibility
    };
    setTotalColumnsVisibility(updatedColumnsVisibility);

    try {
      await updateColumnVisibility
        .mutateAsync(updatedColumnsVisibility)
        .then(() => {
          refreshAuthUserDashboardState();
        });
    } catch (error) {
      toast.error(
        `Failed to update column visibility: ${getErrorMessage(error)}`
      );
    }
  };
  // The Chat column is a chat control on a Screen screen, so it follows the
  // same gate as the application header's and the applicant rows' Chat buttons:
  // the native-chat flag AND the partner's own Chat toggle under Settings >
  // Features. With that toggle off every chat control disappears, this column
  // and its visibility switch included (CRM-1325). Inbox reachability is not
  // enough — a CRM partner with SMS but chat switched off still reaches the
  // Inbox, and used to be offered a "Chat" column anyway.
  // Only the lists passed DOWN are filtered — never the persisted state, or the
  // next toggle would PATCH away the user's real preference.
  const isChatColumnEnabled = type.isNativeChatActive;
  const visibleColumns = useMemo(
    () =>
      isChatColumnEnabled
        ? columnsVisibility
        : columnsVisibility.filter((col) => col.id !== "chat"),
    [columnsVisibility, isChatColumnEnabled]
  );
  const openMessagePopup = useMessagePopupStore((state) => state.open);

  const getApplications = async (
    status?: ApplicationStatus[],
    perPage?: number
  ) => {
    const sorts = sorting[0]
      ? [{ [sorting[0].id]: sorting[0].desc ? "desc" : "asc" }]
      : [];

    let unitListed: boolean | undefined = undefined;
    if (filters?.unitStatus === "listed") unitListed = true;
    else if (filters?.unitStatus === "unlisted") unitListed = false;

    return instance
      .get(
        query +
          "?" +
          queryString.stringify(
            {
              per_page: perPage || searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              search: searchText.trim(),
              status,
              property_ids: filters?.properties?.map((p) => p.id),
              unit_ids: filters?.units?.map((u) => u.id),
              unit_types: filters?.unitTypes?.map((type) =>
                type === NO_UNIT_TYPE ? "" : type
              ),
              cities: filters?.cities,
              screening_statuses: filters?.screeningStatuses,
              unit_listed: unitListed,
              property_group_ids: filters?.propertyGroups?.map((g) => g.id),
              property_id: propertyId,
              unit_id: unitId,
              contact_id: contactId,
              sorts,
              group_by: groupBy || undefined,
              assignees: buildAssigneesParam(assigneeIds)
            },
            {
              arrayFormat: "brackets"
            }
          )
      )
      .then(({ data }: { data: PaginatedResponse<Application> }) => ({
        ...data,
        items: data.items.map((app) => addAssigneeIds(app))
      }));
  };

  const buildQuery = (status: ApplicationStatus) => {
    return {
      queryKey: [
        query + "__total",
        {
          page: 1,
          per_page: 1,
          search: searchText.trim() || undefined,
          status: status,
          property_ids: filters?.properties?.map((p) => p.id),
          unit_ids: filters?.units?.map((u) => u.id),
          unit_types: filters?.unitTypes,
          cities: filters?.cities,
          screening_statuses: filters?.screeningStatuses,
          unit_status: filters?.unitStatus,
          property_group_ids: filters?.propertyGroups?.map((g) => g.id),
          property_id: propertyId,
          unit_id: unitId,
          contact_id: contactId
        }
      ],
      queryFn: () => getApplications([status], 1).then((d) => d.total_count),
      enabled: isInitialized,
      onSuccess: (data) => {
        if (
          status === ApplicationStatus.Submitted &&
          data.total_count > 0 &&
          !searchParams.tab
        )
          setSearchParams({ ...searchParams, tab: ActiveTab.Submitted });
      }
    };
  };

  const [
    { data: totalSubmittedCount },
    { data: totalPartiallySubmittedCount }
  ] = useQueries({
    queries: [
      buildQuery(ApplicationStatus.Submitted),
      buildQuery(ApplicationStatus.PartiallySubmitted)
    ]
  });

  const applicationTabs = useMemo(
    () =>
      buildApplicationTabs({
        submitted: totalSubmittedCount,
        partiallySubmitted: totalPartiallySubmittedCount
      }),
    [totalSubmittedCount, totalPartiallySubmittedCount]
  );

  const totalCountQueryKeys = [
    buildQuery(ApplicationStatus.Submitted).queryKey,
    buildQuery(ApplicationStatus.PartiallySubmitted).queryKey
  ];

  const status = useMemo(() => {
    const statusMap = {
      NotSubmitted: [ApplicationStatus.Pending, ApplicationStatus.Started],
      PartiallySubmitted: [ApplicationStatus.PartiallySubmitted],
      Submitted: [ApplicationStatus.Submitted],
      Approved: [ApplicationStatus.Approved],
      ApprovedConditionally: [ApplicationStatus.ConditionalApproved],
      Declined: [ApplicationStatus.Rejected],
      Canceled: [ApplicationStatus.Canceled],
      OnHold: [ApplicationStatus.OnHold]
    };
    return statusMap[searchParams.tab] ?? [];
  }, [searchParams.tab]);

  const {
    data: applications,
    refetch: refetchApplications,
    isFetching
  } = useQuery({
    queryKey: [
      "APPLICANTS_TABLE",
      query,
      {
        applications: searchText,
        per_page: searchParams.per_page || DEFAULT_PER_PAGE,
        page: searchParams.page || "1",
        status,
        property_ids: filters?.properties?.map((p) => p.id),
        unit_ids: filters?.units?.map((u) => u.id),
        unit_types: filters?.unitTypes,
        cities: filters?.cities,
        screening_statuses: filters?.screeningStatuses,
        property_id: propertyId,
        unit_id: unitId,
        contact_id: contactId,
        sorting,
        group_by: groupBy || undefined,
        unit_status: filters?.unitStatus,
        property_group_ids: filters?.propertyGroups?.map((g) => g.id),
        partner_user_ids: assigneeIds
      }
    ],
    queryFn: () => getApplications(status),
    placeholderData: keepPreviousData,
    enabled: isInitialized
  });

  const tableData = useMemo(() => {
    return applications?.items;
  }, [applications?.items]);

  const shouldShowCreateButton =
    !propertyId && !unitId && !location.pathname.includes("/contacts");

  // Distinct property ids of the currently selected applications, used to
  // restrict bulk-assign options to users who can access all of them.
  const selectedPropertyIds = useMemo(() => {
    const ids = new Set<number>();
    (tableData ?? []).forEach((application) => {
      if (selectedRentersRow[application.id] && application.property?.id)
        ids.add(application.property.id);
    });
    return Array.from(ids);
  }, [tableData, selectedRentersRow]);

  return (
    <>
      <div className="space-y-4">
        {/*
          Toolbar: search and assignee filter on the left, table controls
          (filter, columns, group by) and "Create application" on the right,
          then the status tabs on their own full-width row. The tabs collapse
          into a "More" menu whenever they don't fit (see ApplicationTabs).
        */}
        <div className="flex w-full flex-wrap items-center gap-x-2 gap-y-4">
          <div className="flex items-center gap-2">
            <BaseInput
              id="search"
              placeholder="Search applications"
              value={searchParams.application}
              onChange={(value) =>
                setSearchParams({
                  ...searchParams,
                  page: "1",
                  application: String(value)
                })
              }
              size={InputSize.SM}
              className="!mb-0 w-[271px]"
            />
            <div className="max-w-[300px]">
              <AssigneeFilterPopover
                open={isAssigneeFilterOpen}
                onOpenChange={setIsAssigneeFilterOpen}
                selectedUserIds={assigneeIds}
                onSelectedUserIdsChange={setAssigneeIds}
              />
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Show if={!unitId}>
              <div className="relative">
                <IconButton
                  size={isMobile ? IconButtonSize.BASE : IconButtonSize.SM}
                  variant={IconButtonVariant.BASE}
                  Icon={FilterLines}
                  onClick={() => setShowFilters(true)}
                />
                <Show
                  if={
                    Object.values(filters ?? {}).some((f) => f?.length) ||
                    Boolean(filters?.unitStatus) ||
                    Boolean(filters?.propertyGroups?.length)
                  }
                >
                  <div className="p-1 rounded-full bg-white absolute bottom-[5px] right-[4px]">
                    <div className=" size-2 z-20 bg-primary-500 rounded-full" />
                  </div>
                </Show>
              </div>
            </Show>

            <div className="relative">
              <ColumnsMenu
                activeTab={searchParams.tab as ActiveTab}
                isMobile={isMobile}
                columnsData={visibleColumns}
                onColumnToggle={handleColumnToggle}
              />
            </div>

            <Select
              size={InputSize.SM}
              id="group_by"
              placeholder="Group by"
              className="!mb-0"
              value={groupBy ?? ""}
              onChange={(e) =>
                setGroupBy(e.target.value ? e.target.value : undefined)
              }
              options={[
                {
                  label: "Group application",
                  value: ""
                },
                {
                  label: "Screening status",
                  value: "screening_status"
                },
                {
                  label: dictionary.Unit,
                  value: "unit"
                }
              ]}
              formatOptionLabel={(option, { context }) => {
                const opt = option as BaseOption;
                if (context === "value")
                  return (
                    <span className="font-bold text-content-1000 text-xs">
                      {opt.value ? (
                        <>
                          Group by:{" "}
                          <span className="text-content-300">{opt.label}</span>
                        </>
                      ) : (
                        opt.label
                      )}
                    </span>
                  );
                return <span>{opt.label}</span>;
              }}
            />
          </div>

          <Show if={shouldShowCreateButton}>
            <div className="hidden md:flex">
              <Button
                variant={ButtonVariant.PRIMARY}
                className="w-full md:w-auto"
                size={ButtonSize.SM}
                Icon={Plus}
                onClick={() => setIsShowModal(true)}
              >
                Create application
              </Button>
            </div>
          </Show>

          <div className="w-full min-w-0">
            <ApplicationTabs
              tabs={applicationTabs}
              selected={searchParams.tab as ActiveTab}
              onChange={(t: ActiveTab) => {
                setSearchParams({
                  tab: t,
                  application: searchParams.application,
                  page: "1"
                });
              }}
            />
          </div>
        </div>

        <Show
          if={
            Object.values(filters ?? {}).some((f) => f?.length) ||
            filters?.unitStatus ||
            Boolean(filters?.propertyGroups?.length)
          }
        >
          <div className="flex flex-wrap gap-2">
            {filters?.properties?.map((property) => (
              <FilterBadge
                key={property.id}
                filter={property.name}
                onRemove={() => handleRemoveProperty(property.id)}
              />
            ))}

            {filters?.cities?.map((city) => (
              <FilterBadge
                key={city}
                filter={city}
                onRemove={() => handleRemoveCity(city)}
              />
            ))}

            {filters?.units?.map((unit) => (
              <FilterBadge
                key={unit.id}
                filter={unit.name}
                onRemove={() => handleRemoveUnit(unit.id)}
              />
            ))}

            {filters?.unitTypes?.map((unitType) => (
              <FilterBadge
                key={unitType}
                filter={unitType === NO_UNIT_TYPE ? "—" : unitType}
                onRemove={() => handleRemoveUnitType(unitType)}
              />
            ))}

            {filters?.screeningStatuses?.map((status) => (
              <FilterBadge
                key={status}
                filter={status}
                onRemove={() => handleRemoveScreeningStatus(status)}
              />
            ))}

            {filters?.unitStatus && (
              <FilterBadge
                filter={
                  filters.unitStatus === "listed"
                    ? "Applications allowed"
                    : "Applications not allowed"
                }
                onRemove={() => handleRemoveUnitStatus()}
              />
            )}

            {filters?.propertyGroups?.map((group) => (
              <FilterBadge
                key={group.id}
                filter={group.name}
                onRemove={() => handleRemovePropertyGroup(group.id)}
              />
            ))}
          </div>
        </Show>

        <SelectPopUp
          selectedRow={selectedRentersRow}
          selectedPropertyIds={selectedPropertyIds}
          clearSelectedRows={() => setSelectedRentersRow({})}
          onMergeClick={() => setShowMergeModal(true)}
        />

        <ApplicationsDataGridTable
          groupBy={groupBy}
          viewPortHeightOffset={viewPortHeightOffset}
          setSorting={handleSortingChange}
          sorting={sorting}
          activeTab={searchParams.tab as ActiveTab}
          sortableKeys={sortableKeys}
          selectedRentersRow={selectedRentersRow as Record<string, boolean>}
          setSelectedRentersRow={setSelectedRentersRow}
          applications={tableData ?? []}
          onInviteClick={() => {
            setIsShowModal(true);
          }}
          perPageRows={Number(DEFAULT_PER_PAGE)}
          isLoading={isFetching}
          onCancelClick={(id) => {
            setCancelModalId(id);
          }}
          onUncancelClick={(id) => {
            setUncancelModalId(id);
          }}
          onRefundClick={(id) => setRefundModalId(id)}
          onSplitClick={(id) => setSplitModalId(id)}
          onMoveClick={(id) => setMoveModalId(id)}
          hasSearchParams={!!searchText}
          columnsVisibility={visibleColumns}
          invalidateQueries={[
            ...totalCountQueryKeys,

            [
              "APPLICANTS_TABLE",
              query,
              {
                applications: searchText,
                per_page: searchParams.per_page || DEFAULT_PER_PAGE,
                page: searchParams.page || "1",
                status,
                property_id: propertyId,
                unit_id: unitId,
                sorting
              }
            ]
          ]}
          pagination={
            <Pagination
              className="justify-end"
              total={applications?.page_count as number}
              current={Number(searchParams.page)}
              onChange={(e) =>
                setSearchParams({ ...searchParams, page: String(e) })
              }
            />
          }
          pagesCount={applications?.page_count}
          openApplicationNotes={setShowApplicationNotesId}
          onChatClick={(application) => {
            // List rows carry applicant ids but no CRM contact id — the pop-up
            // resolves (and links, if needed) the contact from the applicant.
            // Skip applicants the pop-up itself would reject: a cancelled or
            // never-started one has no conversation to open. The slim list row
            // has no is_manual/application_started_at, so application_status is
            // the only eligibility signal available here.
            const messageable = application.applicants.filter(
              (item) =>
                item.application_status !== ApplicantStatus.Canceled &&
                item.application_status !== ApplicantStatus.Pending
            );
            const applicant =
              messageable.find(
                (item) => item.role === ApplicantType.Applicant
              ) ?? messageable[0];
            if (!applicant) return;
            openMessagePopup({
              applicantId: applicant.id,
              applicationId: application.id,
              contactName:
                [applicant.first_name, applicant.last_name]
                  .filter(Boolean)
                  .join(" ")
                  .trim() || applicant.email,
              channel: "chat"
            });
          }}
        />
      </div>
      {showMergeModal ? (
        <MergeApplicantsModal
          isOpen={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          selectedApplicants={Object.keys(selectedRentersRow)}
        />
      ) : null}
      {isShowModal && (
        <InviteAndCreateModal
          open={isShowModal}
          setOpen={() => setIsShowModal(false)}
          unitId={unitId}
          propertyId={propertyId}
          onInviteRefreshKey={[
            "APPLICANTS_TABLE",
            query,
            {
              applications: searchText,
              per_page: searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              status,
              property_ids: filters?.properties?.map((p) => p.id),
              unit_ids: filters?.units?.map((u) => u.id),
              unit_types: filters?.unitTypes,
              cities: filters?.cities,
              property_id: propertyId,
              unit_id: unitId,
              sorting
            }
          ]}
        />
      )}
      <Show if={Boolean(cancelModalId)}>
        <CancelOrRefundModal
          applicants={applicants as Applicant[] | undefined}
          applicationId={cancelModalId ?? ""}
          onClose={() => setCancelModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(uncancelModalId)}>
        <UncancelModal
          applicationId={uncancelModalId ?? ""}
          onClose={() => setUncancelModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(splitModalId)}>
        <SplitModal
          isOpen={Boolean(splitModalId)}
          applicationId={splitModalId ?? ""}
          onClose={() => setSplitModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(moveModalId)}>
        <MoveModal
          applicationId={moveModalId ?? ""}
          invalidateQueries={totalCountQueryKeys}
          onClose={() => setMoveModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(refundModalId)}>
        <RefundModal
          queryKey={[
            "APPLICANTS_TABLE",
            query,
            {
              applications: searchText,
              per_page: searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              status,
              property_ids: filters?.properties?.map((p) => p.id),
              unit_ids: filters?.units?.map((u) => u.id),
              unit_types: filters?.unitTypes,
              cities: filters?.cities,
              property_id: propertyId,
              unit_id: unitId,
              sorting
            }
          ]}
          applicationId={refundModalId ?? ""}
          onClose={() => setRefundModalId(undefined)}
        />
      </Show>
      <FiltersDrawer
        unitId={unitId}
        propertyId={propertyId}
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
      />
      <Show if={Boolean(showApplicationNotesId)}>
        <Notes
          showNotes={Boolean(showApplicationNotesId)}
          setShowNotes={() => setShowApplicationNotesId(undefined)}
          application={
            applications?.items.find(
              (item) => item.id === showApplicationNotesId
            ) as Application
          }
          onNoteChange={refetchApplications}
          showViewApplicationButton
          onMarkReadSuccess={() => {
            refetchApplications();
          }}
        />
      </Show>
    </>
  );
};
